/**
 * Single-entry click dispatcher for a MapLibre map instance.
 */

import type maplibregl from "maplibre-gl";

import { buildLayerZIndex } from "./mapClick.tools";
import type { MapClickHit, MapClickProvider } from "./mapClick.types";

/** Per-map coordinator registry; map instances live for the whole app session. */
const coordinators = new WeakMap<maplibregl.Map, MapClickCoordinator>();

/**
 * On each map click, every registered provider hit-tests the point and only the
 * provider of the visually topmost element is invoked.
 *
 * @remarks Element order follows `map.getLayersOrder()` paint order, so the winner
 * is always the element the user actually sees on top — including custom WebGL
 * layers (e.g. vehicles) which are part of the paint order but invisible to
 * `queryRenderedFeatures`. Non-winning providers receive `onMiss` so deselection
 * semantics (vehicle, itinerary leg) keep working exactly like a plain map click.
 */
export class MapClickCoordinator {

    private readonly providers = new Map<string, MapClickProvider>();

    private isListening = false;

    /**
     * @param map Target MapLibre map instance this coordinator dispatches clicks for.
     */
    public constructor( private readonly map: maplibregl.Map ) {
    }

    private readonly handleClick = ( event: maplibregl.MapMouseEvent ): void => {
        const layerZIndex = buildLayerZIndex( this.map );

        let winningProvider: MapClickProvider | null = null;
        let winningHit: MapClickHit | null = null;

        for ( const provider of this.providers.values() ) {
            let hit: MapClickHit | undefined;

            try {
                hit = provider.hitTest( event, layerZIndex );
            } catch( error: unknown ) {
                console.error( `[MapClickCoordinator] hitTest failed for provider "${ provider.id }":`, error );
            }

            if ( hit !== undefined && ( winningHit === null || hit.zIndex > winningHit.zIndex ) ) {
                winningProvider = provider;
                winningHit = hit;
            }
        }

        for ( const provider of this.providers.values() ) {
            if ( provider === winningProvider && winningHit !== null ) {
                provider.onHit?.( winningHit, event );
            } else {
                provider.onMiss?.( event );
            }
        }
    };

    /**
     * Registers or replaces a click provider.
     * The shared map click listener is attached lazily on the first registration.
     *
     * @param provider Provider to register, keyed by {@link MapClickProvider.id}.
     */
    public registerProvider( provider: MapClickProvider ): void {
        if ( !this.isListening ) {
            this.map.on( "click", this.handleClick );
            this.isListening = true;
        }

        this.providers.set( provider.id, provider );
    }

    /**
     * Removes a provider; detaches the shared listener when the registry is empty.
     *
     * @param providerId Id previously passed to {@link registerProvider}.
     */
    public unregisterProvider( providerId: string ): void {
        this.providers.delete( providerId );

        if ( this.providers.size === 0 && this.isListening ) {
            this.map.off( "click", this.handleClick );
            this.isListening = false;
        }
    }

}

/**
 * Returns the click coordinator bound to a map instance, creating it on first access.
 *
 * @param map Active MapLibre map.
 * @returns The shared coordinator for that map.
 */
export function getMapClickCoordinator( map: maplibregl.Map ): MapClickCoordinator {
    let coordinator = coordinators.get( map );

    if ( coordinator === undefined ) {
        coordinator = new MapClickCoordinator( map );
        coordinators.set( map, coordinator );
    }

    return coordinator;
}
